import type { AnalysisConfig, BuildingResult, MetricKey } from '../core/domain/types';
import { relativeChange } from '../core/calc/engine';
import { METRICS } from './metrics';
import { fmt } from './format';

function cmp(v: number, ref: number | undefined, k: MetricKey): string {
  const m = METRICS[k];
  if (ref === undefined) return `${fmt(v, m.decimals)} ${m.unit}`;
  const rel = relativeChange(v, ref);
  if (rel === null) return `${fmt(v, m.decimals)} ${m.unit} (Mittel 0)`;
  const pct = Math.round(Math.abs(rel) * 100);
  if (pct < 3) return `${fmt(v, m.decimals)} ${m.unit}, nahe am Mittel`;
  return `${fmt(v, m.decimals)} ${m.unit}, ${pct} % ${rel > 0 ? 'über' : 'unter'} dem Mittel`;
}

/**
 * Fachlich zurückhaltende Kernaussage: nennt das letzte vollständige Jahr als
 * Abweichung vom Referenzmittel und das Mittel der jüngsten Jahre, ohne
 * Trend- oder Wirkungsbehauptung.
 */
export function keyMessage(r: BuildingResult | undefined, cfg: AnalysisConfig): string | null {
  if (!r || r.lastCompleteYear === null) return null;
  const last = r.years.find((y) => y.year === r.lastCompleteYear)!;
  const refOk = r.reference.status !== 'ungenuegend';
  const means = refOk ? r.reference.means : {};
  const refName = `${r.reference.start}–${r.reference.end}${r.reference.status === 'teil' ? ' (Teilreferenz)' : ''}`;
  const s1 = `${last.year} betrugen die Heizgradtage ${cmp(last.hdd, means.hdd, 'hdd')}, die Kühlgradtage ${cmp(last.cdd, means.cdd, 'cdd')}${refOk ? ` (Mittel ${refName})` : ''}.`;
  const recent = r.years.filter((y) => y.complete && y.year <= cfg.periodEnd && y.year >= cfg.periodStart).slice(-10);
  let s2 = '';
  if (recent.length >= 5 && refOk && means.warmNights !== undefined) {
    const wn = recent.reduce((a, y) => a + y.warmNights, 0) / recent.length;
    const hot = recent.reduce((a, y) => a + y.hotDays, 0) / recent.length;
    s2 = ` Im Mittel ${recent[0].year}–${recent[recent.length - 1].year} traten ${fmt(wn, 1)} warme Nächte und ${fmt(hot, 1)} heisse Tage pro Jahr auf (Referenz ${fmt(means.warmNights, 1)} bzw. ${fmt(means.hotDays, 1)}).`;
  } else if (!refOk) {
    s2 = ' Für einen Vergleich mit 1991–2020 fehlen noch vollständige Jahre.';
  }
  return s1 + s2;
}
